import React from "react";
import { Link } from "@mui/material";
import LinesTable from "./LinesTable";
import moment from "moment/min/moment-with-locales";
import { useTranslation } from "react-i18next";

const STATUS_COLOR = {
  Open:      { bg: "#e3f2fd", color: "#1565c0" },
  Partial:   { bg: "#fff8e1", color: "#e65100" },
  Complete:  { bg: "#e8f5e9", color: "#2e7d32" },
  Cancelled: { bg: "#ffebee", color: "#c62828" },
};

function AddressBlock({ title, account, address }) {
  const addr = address || account?.visit_address;
  return (
    <div style={{ flex: "1 1 0", minWidth: 0 }}>
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase",
        letterSpacing: "0.06em", color: "#888", marginBottom: 6 }}>
        {title}
      </div>
      {account ? (
        <>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>{account.Name}</div>
          {account.Email && (
            <div style={{ fontSize: 13, color: "#555", marginBottom: 6 }}>{account.Email}</div>
          )}
          {addr ? (
            <div style={{ fontSize: 13, color: "#555", lineHeight: 1.7 }}>
              {addr.AddressLine1 && <div>{addr.AddressLine1}</div>}
              {addr.AddressLine2 && <div>{addr.AddressLine2}</div>}
              {addr.AddressLine3 && <div>{addr.AddressLine3}</div>}
              {(addr.Postcode || addr.City) && (
                <div>{[addr.Postcode?.trim(), addr.City].filter(Boolean).join("  ")}</div>
              )}
              {addr.Country && <div>{addr.Country.trim()}</div>}
            </div>
          ) : (
            <div style={{ fontSize: 13, color: "#bbb" }}>—</div>
          )}
        </>
      ) : (
        <div style={{ fontSize: 13, color: "#bbb" }}>—</div>
      )}
    </div>
  );
}

function MetaField({ label, value, children }) {
  if (!value && !children) return null;
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase",
        letterSpacing: "0.06em", color: "#888", marginBottom: 3 }}>
        {label}
      </div>
      {children || <div style={{ fontSize: 14, color: "#222" }}>{value}</div>}
    </div>
  );
}

export default function OrderDetails({ data }) {
  const { t } = useTranslation();
  const statusStyle = STATUS_COLOR[data.Status] || { bg: "#f5f5f5", color: "#555" };

  return (
    <div style={{ width: "100%", fontFamily: "inherit" }}>

      {/* Header */}
      <div style={{
        display: "flex", alignItems: "flex-start", justifyContent: "space-between",
        flexWrap: "wrap", gap: 16, padding: "20px 24px",
        background: "#fafafa", border: "1px solid #ebebeb",
        borderRadius: "8px 8px 0 0", borderBottom: "none",
      }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase",
            letterSpacing: "0.06em", color: "#888" }}>{t("Order Number")}</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: "#cc9966", marginTop: 2 }}>
            #{data.OrderNumber}
          </div>
        </div>
        <div style={{ display: "flex", gap: 32, flexWrap: "wrap" }}>
          <MetaField label={t("Order Date")}
            value={moment(data.OrderDate).locale("nl").format("LL")} />
          {data.DeliveryDate && (
            <MetaField label={t("Delivery Date")}
              value={moment(data.DeliveryDate).locale("nl").format("LL")} />
          )}
          <MetaField label={t("Reference")} value={data.YourRef || "—"} />
          <MetaField label={t("Description")} value={data.Description || "—"} />
          <MetaField label={t("Status")}>
            <span style={{
              display: "inline-block", padding: "3px 12px", borderRadius: 12,
              fontSize: 12, fontWeight: 600,
              background: statusStyle.bg, color: statusStyle.color,
            }}>{t(data.Status)}</span>
          </MetaField>
          {data.invoices?.length > 0 && (
            <MetaField label={t("Invoices")}>
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {data.invoices.map((inv) => (
                  <Link key={inv.InvoiceID} href={"/my-account/invoices/" + inv.InvoiceID}
                    style={{ fontSize: 13 }}>
                    #{inv.InvoiceNumber}
                  </Link>
                ))}
              </div>
            </MetaField>
          )}
        </div>
      </div>

      {/* Addresses */}
      <div style={{ display: "flex", border: "1px solid #ebebeb", borderBottom: "none" }}>
        {[
          { title: t("Ordered By"),
            account: data.ordered_by_account,
            address: data.ordered_by_account?.visit_address },
          { title: t("Deliver To"),
            account: data.delivery_to_account,
            address: data.delivery_to_account?.delivery_address || data.delivery_to_account?.visit_address },
          { title: t("Invoice To"),
            account: data.invoice_to_account,
            address: data.invoice_to_account?.invoice_address || data.invoice_to_account?.visit_address },
        ].map((block, i, arr) => (
          <div key={i} style={{
            flex: 1, padding: "20px 24px",
            borderRight: i < arr.length - 1 ? "1px solid #ebebeb" : "none",
          }}>
            <AddressBlock {...block} />
          </div>
        ))}
      </div>

      {/* Lines table */}
      <div style={{ border: "1px solid #ebebeb", borderRadius: "0 0 8px 8px", overflow: "hidden" }}>
        <LinesTable data={data.lines} />
      </div>

    </div>
  );
}
